import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AdminTenantService } from '@app/services/admin-tenant/admin-tenant.service';

import { ButtonComponent } from '@app/components/button';
import { ChipColor, ChipComponent, ChipSize } from '@app/components/chip';
import { ConfirmDialogComponent } from '@app/components/confirm-dialog';
import { HeadingComponent } from '@app/components/heading';
import { InputComponent, InputIcon } from '@app/components/input';
import { SpinnerComponent } from '@app/components/spinner';
import { ColumnType, TableComponent, TableConfig } from '@app/components/table';
import { TextComponent } from '@app/components/text';
import { ToastService, ToastType } from '@app/components/toast';
import { ViewComponent, ViewJustify } from '@app/components/view';
import { AdminChallengesV2Service } from '@app/services/admin-challenges-v2/admin-challenges-v2.service';
import { AdminChallengesService } from '@app/services/admin-challenges/admin-challenges.service';
import {
  Challenge,
  ChallengeService,
  getMetricChallengeStatus,
  MetricChallengeViewStatus,
} from '@app/services/challenge/challenge.service';
import { TranslatorService } from '@app/services/translator/translator.service';
import { Spacing } from '@app/shared/types';
import { TranslatePipe } from '@ngx-translate/core';
import {
  GroupChallengeFilter,
  GroupChallengeSortColumn,
  GroupChallengeSortOrder,
} from '@skyfit/core-data-library';
import { forkJoin } from 'rxjs';
import { GroupChallengeRoute } from './challenges-route';

export enum GroupChallengeOptions {
  NONE = '',
  EDIT = 'edit',
  DELETE = 'delete',
  DUPLICATE = 'duplicate',
  VIEW = 'view',
}

enum GroupChallengeStatus {
  NOT_STARTED = 'notStarted',
  STARTED = 'started',
  ENDED = 'ended',
  NOT_SET = 'notSet',
  LIVE = 'live',
}

const METRIC_STATUS_TO_ROW_STATUS: Record<
  MetricChallengeViewStatus,
  GroupChallengeStatus
> = {
  [MetricChallengeViewStatus.NOT_STARTED]: GroupChallengeStatus.NOT_STARTED,
  [MetricChallengeViewStatus.STARTED]: GroupChallengeStatus.STARTED,
  [MetricChallengeViewStatus.ENDED]: GroupChallengeStatus.ENDED,
  [MetricChallengeViewStatus.NOT_SET]: GroupChallengeStatus.NOT_SET,
};

type ChallengeTableRow = {
  title: string;
  status: GroupChallengeStatus;
  startDate: string;
  endDate: string;
  signupDate: string;
  _startDateRaw: number;
  _endDateRaw: number;
  _signupDateRaw: number;
  options: { key: string; label: string }[];
  chipColor: ChipColor;
  groupChallengeId?: number;
  id?: string;
};

@Component({
  selector: 'app-challenges',
  templateUrl: './challenges.component.html',
  styleUrl: './challenges.component.css',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    TableComponent,
    SpinnerComponent,
    HeadingComponent,
    InputComponent,
    TextComponent,
    ViewComponent,
    ButtonComponent,
    ConfirmDialogComponent,
    ChipComponent,
  ],
})
export class ChallengesComponent implements OnInit {
  private readonly translatorService = inject(TranslatorService);
  private readonly adminTenantService = inject(AdminTenantService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly messageService = inject(ToastService);
  private readonly challengesService = inject(AdminChallengesService);
  private readonly challengesServicev2 = inject(AdminChallengesV2Service);
  private readonly stepChallengeService = inject(ChallengeService);
  dialogOpen = signal(false);
  isLoading = signal(true);
  searchInput = signal('');
  processedChallenges = signal<ChallengeTableRow[]>([]);
  statusFilter = signal<GroupChallengeStatus | null>(null);
  filteredChallenges = computed(() => {
    const filter = this.statusFilter();
    if (!filter) return this.processedChallenges();
    return this.processedChallenges().filter((c) => c.status === filter);
  });
  hasChallenges = computed(() => this.filteredChallenges().length > 0);
  challengeId = signal(0);
  stepChallengeId = signal('');
  selectedStatus = signal<GroupChallengeStatus | null>(null);
  challengeAction = signal(GroupChallengeOptions.NONE);
  readonly currentDate = new Date();
  readonly statusOptions: {
    label: string;
    value: GroupChallengeStatus | null;
  }[] = [
    { label: 'common.all', value: null },
    {
      label: 'groupChallenges.notStarted',
      value: GroupChallengeStatus.NOT_STARTED,
    },
    { label: 'groupChallenges.started', value: GroupChallengeStatus.STARTED },
    { label: 'groupChallenges.ended', value: GroupChallengeStatus.ENDED },
  ];
  readonly tableConfig: TableConfig = {
    type: 'challenges.challenges',
    columns: [
      {
        key: 'title',
        width: '336px',
        label: 'challenges.challengeName',
        maxLength: 36,
        sortable: true,
      },
      {
        key: 'status',
        width: '120.75px',
        label: 'challenges.status',
        columnType: ColumnType.chip,
        sortable: true,
      },
      {
        key: 'signupDate',
        width: '120.75px',
        label: 'challenges.signupDate',
        sortable: true,
        sortKey: '_signupDateRaw',
      },
      {
        key: 'startDate',
        width: '120.75px',
        label: 'challenges.startDate',
        sortable: true,
        sortKey: '_startDateRaw',
      },
      {
        key: 'endDate',
        width: '120.75px',
        label: 'challenges.endDate',
        sortable: true,
        sortKey: '_endDateRaw',
      },
      {
        key: 'options',
        label: '',
        columnType: ColumnType.options,
      },
    ],
  };
  ViewJustify = ViewJustify;
  Spacing = Spacing;
  InputIcon = InputIcon;
  ChipSize = ChipSize;
  ChipColor = ChipColor;
  GroupChallengeStatus = GroupChallengeStatus;

  setStatusFilter(value: GroupChallengeStatus | null): void {
    this.statusFilter.set(value);
  }

  ngOnInit(): void {
    this.getGroupChallenges();
  }

  sendToastMessage(message: string, type: ToastType) {
    this.messageService.add(message, type);
  }

  stopEventPropagation(event): void {
    if (event.stopEventPropagation) {
      event.stopPropagation();
    }
    event.cancelBubble = true;
  }

  getGroupChallenges(): void {
    this.isLoading.set(true);

    const filter: GroupChallengeFilter = {
      startDates: true,
      tenant: this.adminTenantService.getTenantId(),
      sortColumn: GroupChallengeSortColumn.startDate,
      sortOrder: GroupChallengeSortOrder.descendingNullsLast,
      ...(this.searchInput() && { name: this.searchInput() }),
    };

    forkJoin([
      this.challengesService.searchChallenges(filter),
      this.stepChallengeService.searchMetricChallenges(
        this.adminTenantService.getTenantId()
      ),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([groupChallenges, metricChallenges]) => {
          const formattedChallenges = groupChallenges.groupChallenges
            .filter((c) => !!c.groupChallengeStarts?.length)
            .map((challenge) => {
              let startDate = challenge?.groupChallengeStarts?.[0]?.startTime;
              let endDate: Date | undefined;
              let status: GroupChallengeStatus = GroupChallengeStatus.NOT_SET;
              if (startDate) {
                startDate = new Date(startDate);
                endDate = new Date(startDate);
                endDate.setDate(endDate.getDate() + challenge.durationInDays);
                status = this.getChallengeStatus(startDate, endDate);
              }
              const signupTime =
                challenge?.groupChallengeStarts?.[0]?.signupTime;
              const signupDate = signupTime
                ? this._formatDate(new Date(signupTime))
                : '';
              return {
                ...challenge,
                status,
                startDate: this._formatDate(startDate),
                endDate: this._formatDate(endDate),
                signupDate,
                _startDateRaw: startDate ? startDate.getTime() : 0,
                _endDateRaw: endDate ? endDate.getTime() : 0,
                _signupDateRaw: signupTime ? new Date(signupTime).getTime() : 0,
                options: this.getRowOptions(status),
                chipColor: this.getChipColor(status),
              };
            });
          const formattedMetricChallenges = metricChallenges.map((challenge) =>
            this.decorateMetricChallenge(challenge)
          );

          this.processedChallenges.set(
            [...formattedChallenges, ...formattedMetricChallenges].sort(
              (a, b) => b._endDateRaw - a._endDateRaw
            )
          );
          this.isLoading.set(false);
        },
        error: (error) => {
          console.error(error);
        },
        complete: () => {
          this.isLoading.set(false);
        },
      });
  }

  getChallengeStatus(startDate: Date, endDate: Date): GroupChallengeStatus {
    if (this.currentDate < startDate) {
      return GroupChallengeStatus.NOT_STARTED;
    } else if (this.currentDate >= startDate && this.currentDate <= endDate) {
      return GroupChallengeStatus.STARTED;
    } else if (this.currentDate > endDate) {
      return GroupChallengeStatus.ENDED;
    } else {
      return GroupChallengeStatus.NOT_SET;
    }
  }

  getChipColor(status: GroupChallengeStatus): ChipColor {
    if (status === GroupChallengeStatus.NOT_STARTED) {
      return ChipColor.NAVY;
    }
    if (status === GroupChallengeStatus.STARTED) {
      return ChipColor.BLUE;
    }
    return ChipColor.NEUTRAL;
  }

  decorateMetricChallenge(challenge: Challenge) {
    const startDate = this.toValidDate(challenge.startDate);
    const endDate = this.toValidDate(challenge.endDate);
    const signupTime = this.toValidDate(challenge.enrollmentStartDate);
    const status =
      startDate && endDate
        ? METRIC_STATUS_TO_ROW_STATUS[
            getMetricChallengeStatus(startDate, endDate, this.currentDate)
          ]
        : GroupChallengeStatus.NOT_SET;

    return {
      ...challenge,
      title: challenge.name,
      status,
      startDate: this._formatDate(startDate),
      endDate: this._formatDate(endDate),
      signupDate: signupTime ? this._formatDate(signupTime) : '',
      _startDateRaw: startDate?.getTime() ?? 0,
      _endDateRaw: endDate?.getTime() ?? 0,
      _signupDateRaw: signupTime?.getTime() ?? 0,
      options: this.getMetricRowOptions(status),
      chipColor: this.getChipColor(status),
    };
  }

  // Treat unparseable dates as missing so _formatDate never calls toISOString
  // on an Invalid Date (it throws a RangeError).
  private toValidDate(value?: string): Date | undefined {
    if (!value) return undefined;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
  }

  private isEditable(status: GroupChallengeStatus | null): boolean {
    return (
      status === GroupChallengeStatus.NOT_STARTED ||
      status === GroupChallengeStatus.NOT_SET
    );
  }

  private getMetricRowOptions(status: GroupChallengeStatus) {
    return this.isEditable(status)
      ? [
          { key: GroupChallengeOptions.EDIT, label: 'common.edit' },
          { key: GroupChallengeOptions.DELETE, label: 'common.delete' },
        ]
      : [{ key: GroupChallengeOptions.VIEW, label: 'common.view' }];
  }

  private getRowOptions(status: GroupChallengeStatus) {
    return this.isEditable(status)
      ? [
          { key: GroupChallengeOptions.EDIT, label: 'common.edit' },
          { key: GroupChallengeOptions.DELETE, label: 'common.delete' },
          { key: GroupChallengeOptions.DUPLICATE, label: 'common.duplicate' },
        ]
      : [
          { key: GroupChallengeOptions.VIEW, label: 'common.view' },
          { key: GroupChallengeOptions.DUPLICATE, label: 'common.duplicate' },
        ];
  }

  onOptionSelected(option): void {
    this.challengeId.set(option.groupChallengeId ?? 0);
    this.stepChallengeId.set(
      !option.groupChallengeId && option.id ? option.id : ''
    );
    this.selectedStatus.set(option.status ?? null);
    this.challengeAction.set(option.key);
    switch (option.key) {
      case GroupChallengeOptions.EDIT:
        if (this.stepChallengeId()) {
          this.router.navigate([
            'challenges',
            'steps',
            this.stepChallengeId(),
            'edit',
          ]);
          break;
        }
        this.router.navigate(
          [
            'challenges',
            'edit',
            this.challengesService.encodeChallengeId(this.challengeId()),
          ],
          {
            queryParams: { from: GroupChallengeRoute.challenges },
          }
        );
        break;
      case GroupChallengeOptions.DUPLICATE:
        this.router.navigate(
          [
            'challenges',
            'duplicate',
            this.challengesService.encodeChallengeId(this.challengeId()),
          ],
          {
            queryParams: { from: GroupChallengeRoute.challenges },
          }
        );
        break;
      case GroupChallengeOptions.DELETE: {
        if (!this.isEditable(this.selectedStatus())) {
          return;
        }
        this.dialogOpen.set(true);
        break;
      }
      case GroupChallengeOptions.VIEW:
        if (this.stepChallengeId()) {
          this.router.navigate(['challenges', 'steps', this.stepChallengeId()]);
          break;
        }
        this.router.navigate(['challenges', 'live', this.challengeId()]);
    }
  }

  closeDialog(): void {
    this.dialogOpen.set(false);
    this.challengeId.set(0);
    this.stepChallengeId.set('');
    this.selectedStatus.set(null);
  }

  addChallenge(): void {
    this.router.navigate(['challenges', 'select']);
  }

  deleteChallenge(): void {
    if (!this.isEditable(this.selectedStatus())) {
      this.sendToastMessage(
        this.translatorService.translate('challenges.cannotDelete'),
        ToastType.INFO
      );
      this.closeDialog();
      return;
    }

    const stepId = this.stepChallengeId();
    const groupId = this.challengeId();
    const request = stepId
      ? this.stepChallengeService.deleteChallenge(stepId)
      : groupId
      ? this.challengesServicev2.deleteGroupChallenge(groupId)
      : null;

    if (!request) {
      return;
    }

    request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        const message = this.translatorService.translate(
          'challenges.challengeDeleted'
        );
        this.sendToastMessage(message, ToastType.INFO);
        this.processedChallenges.set(
          this.processedChallenges().filter((challenge) =>
            stepId
              ? challenge.id !== stepId
              : challenge.groupChallengeId !== groupId
          )
        );
        this.closeDialog();
      },
      error: (err) => {
        console.error(err);
        const message = this.translatorService.translate(
          'challenges.cannotDelete'
        );
        this.sendToastMessage(message, ToastType.INFO);
      },
    });
  }

  private _formatDate(date?: Date): string {
    if (!date) return 'n/a';
    const [year, month, day] = date.toISOString().split('T')[0].split('-');
    return `${month}/${day}/${year.substring(2)}`;
  }
}
